import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { Main as Container } from 'Components/Main';
import { PageHelper } from 'Components/PageHelper';
import { PageLoaderList } from 'Components/PageLoader/List';
import { NoData } from 'Components/NoData';
import { Controller, useForm } from 'react-hook-form';
import { getSystemsShortPaged } from 'services/systems-service';
import { archiveSystem, deleteSystem, unarchiveSystem } from 'services/systems-service';
import { getUsersName } from 'services/users-service';
import { useAuthTenant } from 'store/auth';
import { useSelector } from 'react-redux';
import { Dialog, PageHeader, Select, TextField } from 'Components/ui';
import styled from 'styled-components';

const INK = 'var(--vo-ink)';
const MUTED = 'var(--vo-slate)';
const BLUE = 'var(--vo-celeste)';
const BLUE_DEEP = 'var(--vo-cobalto)';
const BORDER = 'var(--vo-line)';
const BG = 'var(--vo-mist)';
const TEXT = 'var(--vo-ink-2)';
const PAGE_SIZE = 15;

/* ── Layout ── */
const AddBtn = styled.button`
  height: 44px;
  background: ${BLUE};
  color: white;
  border: none;
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 20px;
  display: flex;
  align-items: center;
  gap: 7px;
  flex-shrink: 0;
  &:hover { background: ${BLUE_DEEP}; }
`;

/* ── Info banner ── */
const InfoBanner = styled.div`
  background: white;
  border-radius: 14px;
  padding: 16px 20px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  border-left: 4px solid ${BLUE};
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
`;
const InfoText = styled.div``;
const InfoTitle = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${INK};
  margin: 0 0 2px;
`;
const InfoDesc = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: ${MUTED};
  margin: 0;
  line-height: 1.55;
`;

/* ── Filter card ── */
const FilterCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 24px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  margin-bottom: 16px;
`;
const FilterHeader = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: ${MUTED};
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin: 0 0 16px;
`;
const FilterRow = styled.div`
  display: grid;
  gap: 14px;
  margin-bottom: 14px;
  ${({ $cols }) => $cols && `grid-template-columns: ${$cols};`}
`;
const FilterActions = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 18px;
`;
const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;
const FieldLabel = styled.label`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: ${MUTED};
`;
const ClearBtn = styled.button`
  height: 40px;
  background: white;
  color: ${MUTED};
  border: 1.5px solid ${BORDER};
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 20px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const FilterBtn = styled.button`
  height: 40px;
  background: ${BLUE};
  color: white;
  border: none;
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 20px;
  display: flex;
  align-items: center;
  gap: 7px;
  &:hover { background: ${BLUE_DEEP}; }
`;

/* ── Table ── */
const TableCard = styled.div`
  background: white;
  border-radius: 14px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  overflow: hidden;
`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;
const THead = styled.thead`
  background: ${BG};
`;
const Th = styled.th`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: ${MUTED};
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 12px 16px;
  text-align: ${({ $center }) => $center ? 'center' : 'left'};
  border-bottom: 1px solid var(--vo-cloud);
  white-space: nowrap;
`;
const Tr = styled.tr`
  border-bottom: 1px solid ${BG};
  &:hover { background: var(--vo-capri-25); }
  &:last-child { border-bottom: none; }
`;
const Td = styled.td`
  padding: 12px 16px;
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: ${TEXT};
  text-align: ${({ $center }) => $center ? 'center' : 'left'};
`;
const TdName = styled(Td)`
  font-size: 14px;
  color: ${INK};
  font-weight: 500;
`;
const Badge = styled.span`
  display: inline-flex;
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 20px;
  white-space: nowrap;
  color: ${({ $color }) => $color};
  background: ${({ $bg }) => $bg};
`;
const ActionCell = styled.td`
  padding: 8px 16px;
  text-align: center;
`;
const ActionsWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
`;
const IconBtn = styled.button`
  width: 30px;
  height: 30px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${MUTED};
  &:hover {
    background: ${({ $del }) => $del ? 'var(--vo-danger-10)' : 'var(--vo-info-10)'};
    color: ${({ $del }) => $del ? 'var(--vo-danger)' : BLUE};
  }
`;

/* ── Pagination ── */
const PaginationRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  border-top: 1px solid ${BG};
`;
const PaginationInfo = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: ${MUTED};
`;
const PageButtons = styled.div`
  display: flex;
  gap: 4px;
`;
const PageBtn = styled.button`
  width: 32px;
  height: 32px;
  border: ${({ $active }) => $active ? 'none' : `1px solid ${BORDER}`};
  background: ${({ $active }) => $active ? BLUE : 'white'};
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: ${({ $active }) => $active ? '700' : '400'};
  color: ${({ $active }) => $active ? 'white' : MUTED};
  &:disabled { opacity: 0.4; cursor: default; }
`;

/* ── Helpers ── */
const CRIT_MAP = {
  '1': { color: 'var(--vo-success)', bg: 'var(--vo-success-10)' },
  '2': { color: BLUE,      bg: 'var(--vo-info-10)' },
  '3': { color: 'var(--vo-warning)', bg: 'var(--vo-warning-10)' },
  '4': { color: 'var(--vo-sev-high)', bg: 'var(--vo-sev-high-12)' },
  '5': { color: 'var(--vo-danger)', bg: 'var(--vo-danger-10)' },
};

const getCriticityInfo = (level, t) => {
  const styles = CRIT_MAP[String(level)] || { color: MUTED, bg: BG };
  const short = t(`mapping:systems.criticality.short.${level}`, '');
  return { ...styles, label: short ? `${short} (${level})` : String(level ?? '—') };
};

const getBoolInfo = (val, t) => {
  const yes = val === true || val === 'Sim' || val === 'sim' || val === 'true';
  return yes
    ? { label: t('mapping:systems.list.filters.options.yes'), color: 'var(--vo-success)', bg: 'var(--vo-success-10)' }
    : { label: t('mapping:systems.list.filters.options.no'),  color: MUTED,      bg: 'var(--vo-slate-10)' };
};

const getPageButtons = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  if (current <= 4) return [1, 2, 3, 4, 5, '…', total];
  if (current >= total - 3) return [1, '…', total - 4, total - 3, total - 2, total - 1, total];
  return [1, '…', current - 1, current, current + 1, '…', total];
};

export const Systems = () => {
  const { t } = useTranslation(['mapping']);
  const navigate = useNavigate();
  const selectedTenant = useSelector(useAuthTenant);
  const [refetch, setRefetch] = useState(false);
  const [page, setPage] = useState(1);

  const { register, control, reset, getValues } = useForm({
    defaultValues: { name: '', criticity: '', user: '', saas: 'Todos', developedInHouse: 'Todos', status: '' },
  });

  const getContentSearch = () => {
    const d = getValues();
    let qs = `&developedInHouse=${d.developedInHouse}&saas=${d.saas}`;
    if (d.status === 'active') qs += '&archived=false';
    else if (d.status === 'archived') qs += '&archived=true';
    else qs += '&archived=false';
    if (d.name !== '') qs += `&name=${d.name}`;
    if (d.user !== '') qs += `&user=${d.user}`;
    if (d.criticity !== '') qs += `&criticity=${d.criticity}`;
    return qs;
  };

  const { data: users } = useQuery(['getUsers'], async () => {
    if (!selectedTenant.slug) return [];
    const result = await getUsersName(selectedTenant.slug);
    return result?.data?.length > 0 ? result.data : [];
  });

  const { isLoading, error, data: pagedSystems } = useQuery(['getSystems', refetch, page], async () => {
    if (!selectedTenant.slug) return { data: [], page: 1, totalPages: 0 };
    const qs = getContentSearch();
    const result = await getSystemsShortPaged(selectedTenant.slug, page, PAGE_SIZE, qs);
    if (result?.data?.data?.length > 0) return result.data;
    return { data: [], page, totalPages: 0 };
  });

  const onDelete = async (item) => {
    const confirmed = await Dialog.confirm({
      title: t('mapping:systems.list.modals.deleteMessage', { name: item.name }),
      confirmLabel: t('mapping:systems.toasts.deleted'),
      cancelLabel: t('mapping:systems.list.filters.clear'),
    });
    if (!confirmed) return;
    try {
      const res = await deleteSystem(item._id);
      if (res.status === 200 || res.status === 204) {
        Dialog.fire({ icon: 'success', title: t('mapping:systems.toasts.successTitle'), text: t('mapping:systems.toasts.deleted'), timer: 1500, showConfirmButton: false });
        setRefetch(r => !r);
      }
    } catch (e) {
      Dialog.alert({ icon: 'error', title: t('mapping:systems.toasts.errorTitle'), message: e.response?.data?.message ?? e.message });
    }
  };

  const onArchive = async (item) => {
    const confirmed = await Dialog.confirm({
      title: t('mapping:systems.list.modals.archiveMessage', { name: item.name }),
      confirmLabel: t('mapping:systems.toasts.archived'),
      cancelLabel: t('mapping:systems.list.filters.clear'),
    });
    if (!confirmed) return;
    try {
      const res = await archiveSystem(item._id);
      if (res.status === 200 || res.status === 204) {
        Dialog.fire({ icon: 'success', title: t('mapping:systems.toasts.successTitle'), text: t('mapping:systems.toasts.archived'), timer: 1500, showConfirmButton: false });
        setRefetch(r => !r);
      }
    } catch (e) {
      Dialog.alert({ icon: 'error', title: t('mapping:systems.toasts.errorTitle'), message: e.response?.data?.message ?? e.message });
    }
  };

  const onUnarchive = async (item) => {
    const confirmed = await Dialog.confirm({
      title: t('mapping:systems.list.modals.unarchiveMessage', { name: item.name }),
      confirmLabel: t('mapping:systems.toasts.unarchived'),
      cancelLabel: t('mapping:systems.list.filters.clear'),
    });
    if (!confirmed) return;
    try {
      const res = await unarchiveSystem(item._id);
      if (res.status === 200 || res.status === 204) {
        Dialog.fire({ icon: 'success', title: t('mapping:systems.toasts.successTitle'), text: t('mapping:systems.toasts.unarchived'), timer: 1500, showConfirmButton: false });
        setRefetch(r => !r);
      }
    } catch (e) {
      Dialog.alert({ icon: 'error', title: t('mapping:systems.toasts.errorTitle'), message: e.response?.data?.message ?? e.message });
    }
  };

  const tourSteps = [
    { target: 'body', content: t('mapping:systems.tour.list.welcome'), placement: 'center', disableBeacon: true },
    { target: '[data-tour="add-button"]', content: t('mapping:systems.tour.list.addButton'), disableScrolling: true },
    { target: '[data-tour="info-card"]', content: t('mapping:systems.tour.list.infoCard'), disableScrolling: true },
    { target: '[data-tour="filter-card"]', content: t('mapping:systems.tour.list.filterCard'), disableScrolling: true },
    { target: '[data-tour="table"]', content: t('mapping:systems.tour.list.table') },
    { target: '[data-tour="pagination"]', content: t('mapping:systems.tour.list.pagination') },
  ];

  const systems = pagedSystems?.data ?? [];
  const totalPages = pagedSystems?.totalPages ?? 0;
  const currentCount = systems.length;
  const totalItems = pagedSystems?.total ?? (totalPages <= 1 ? currentCount : totalPages * PAGE_SIZE);
  const pageButtons = getPageButtons(page, totalPages);

  const boolOptions = [
    { value: 'Todos', label: t('mapping:systems.list.filters.options.all') },
    { value: 'Sim',   label: t('mapping:systems.list.filters.options.yes') },
    { value: 'Não',   label: t('mapping:systems.list.filters.options.no') },
  ];

  return (
    <Container>
      <PageHelper steps={tourSteps} tourKey="systems-list" />

      <PageHeader
        data-tour="page-title"
        title={t('mapping:systems.list.title')}
        segments={[
          { label: t('common:breadcrumbs.mapping') },
          { label: t('mapping:systems.list.title') },
        ]}
        actions={
          <AddBtn data-tour="add-button" onClick={() => navigate('/systems/add')}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            {t('mapping:systems.list.addButton')}
          </AddBtn>
        }
      />

      <InfoBanner data-tour="info-card">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={BLUE} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
          <rect x="2" y="3" width="20" height="14" rx="2"/>
          <line x1="8" y1="21" x2="16" y2="21"/>
          <line x1="12" y1="17" x2="12" y2="21"/>
        </svg>
        <InfoText>
          <InfoTitle>{t('mapping:systems.list.infoTitle')}</InfoTitle>
          <InfoDesc>{t('mapping:systems.list.infoDescription')}</InfoDesc>
        </InfoText>
      </InfoBanner>

      {!error && (
        <FilterCard data-tour="filter-card">
          <FilterHeader>{t('mapping:systems.list.filters.sectionTitle')}</FilterHeader>

          <FilterRow $cols="2fr 1fr 1fr">
            <FieldGroup>
              <FieldLabel>{t('mapping:systems.list.filters.name')}</FieldLabel>
              <TextField fullWidth {...register('name')} placeholder="" />
            </FieldGroup>
            <FieldGroup>
              <FieldLabel>{t('mapping:systems.list.filters.criticality')}</FieldLabel>
              <Controller control={control} name="criticity" render={({ field: { onChange, value } }) => (
                <Select
                  name="criticity"
                  value={value}
                  onChange={onChange}
                  options={[
                    { value: '', label: t('mapping:systems.list.filters.selectCriticality') },
                    ...['1','2','3','4','5'].map(v => ({ value: v, label: t(`mapping:systems.criticality.${v}`) })),
                  ]}
                />
              )} />
            </FieldGroup>
            <FieldGroup>
              <FieldLabel>{t('mapping:systems.list.filters.responsible')}</FieldLabel>
              <Controller control={control} name="user" render={({ field: { onChange, value } }) => (
                <Select
                  name="user"
                  value={value}
                  onChange={onChange}
                  options={[
                    { value: '', label: t('mapping:systems.list.filters.selectResponsible') },
                    ...(users || []).map(u => ({ value: u.name, label: u.name })),
                  ]}
                />
              )} />
            </FieldGroup>
          </FilterRow>

          <FilterRow $cols="1fr 1fr 1fr">
            <FieldGroup>
              <FieldLabel>{t('mapping:systems.list.filters.saas')}</FieldLabel>
              <Controller control={control} name="saas" render={({ field: { onChange, value } }) => (
                <Select name="saas" value={value} onChange={onChange} options={boolOptions} />
              )} />
            </FieldGroup>
            <FieldGroup>
              <FieldLabel>{t('mapping:systems.list.filters.developedInHouse')}</FieldLabel>
              <Controller control={control} name="developedInHouse" render={({ field: { onChange, value } }) => (
                <Select name="developedInHouse" value={value} onChange={onChange} options={boolOptions} />
              )} />
            </FieldGroup>
            <FieldGroup>
              <FieldLabel>{t('mapping:systems.list.filters.status')}</FieldLabel>
              <Controller control={control} name="status" render={({ field: { onChange, value } }) => (
                <Select
                  name="status"
                  value={value}
                  onChange={onChange}
                  options={[
                    { value: '', label: t('mapping:systems.list.filters.selectStatus') },
                    { value: 'all', label: t('mapping:systems.list.filters.statusAll') },
                    { value: 'active', label: t('mapping:systems.list.filters.statusActive') },
                    { value: 'archived', label: t('mapping:systems.list.filters.statusArchived') },
                  ]}
                />
              )} />
            </FieldGroup>
          </FilterRow>

          <FilterActions>
            <ClearBtn type="button" onClick={() => { reset(); setRefetch(r => !r); }}>
              {t('mapping:systems.list.filters.clear')}
            </ClearBtn>
            <FilterBtn type="button" onClick={() => { setPage(1); setRefetch(r => !r); }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              {t('mapping:systems.list.filters.filter')}
            </FilterBtn>
          </FilterActions>
        </FilterCard>
      )}

      {isLoading && <PageLoaderList />}
      {error && <div style={{ padding: 24 }}>{t('mapping:systems.list.errorLoad')}</div>}
      {!isLoading && !error && systems.length === 0 && <NoData description={t('mapping:systems.list.emptyState')} />}

      {!isLoading && !error && systems.length > 0 && (
        <TableCard data-tour="table">
          <Table>
            <THead>
              <tr>
                <Th>{t('mapping:systems.table.name')}</Th>
                <Th>{t('mapping:systems.table.criticality')}</Th>
                <Th $center>{t('mapping:systems.table.saas')}</Th>
                <Th $center>{t('mapping:systems.table.developedInHouse')}</Th>
                <Th>{t('mapping:systems.table.responsible')}</Th>
                <Th $center>{t('mapping:systems.list.table.actions')}</Th>
              </tr>
            </THead>
            <tbody>
              {systems.map(sys => {
                const crit = getCriticityInfo(sys.criticity, t);
                const saas = getBoolInfo(sys.saas, t);
                const dev  = getBoolInfo(sys.developedInHouse, t);
                return (
                  <Tr key={sys._id}>
                    <TdName>{sys.name}</TdName>
                    <Td>
                      <Badge $color={crit.color} $bg={crit.bg}>{crit.label}</Badge>
                    </Td>
                    <Td $center>
                      <Badge $color={saas.color} $bg={saas.bg}>{saas.label}</Badge>
                    </Td>
                    <Td $center>
                      <Badge $color={dev.color} $bg={dev.bg}>{dev.label}</Badge>
                    </Td>
                    <Td>{sys.user}</Td>
                    <ActionCell>
                      <ActionsWrap>
                        <IconBtn type="button" title={t('mapping:systems.show.editLink')} onClick={() => navigate(`/systems/show/${sys._id}`)}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                          </svg>
                        </IconBtn>
                        <IconBtn type="button" title={t('mapping:systems.show.editLink')} onClick={() => navigate(`/systems/edit/${sys._id}`)}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                          </svg>
                        </IconBtn>
                        {!sys.archived ? (
                          <IconBtn type="button" title={t('mapping:systems.toasts.archived')} onClick={() => onArchive(sys)}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/>
                            </svg>
                          </IconBtn>
                        ) : (
                          <IconBtn type="button" title={t('mapping:systems.toasts.unarchived')} onClick={() => onUnarchive(sys)}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5"/>
                              <polyline points="10 12 12 10 14 12"/><line x1="12" y1="10" x2="12" y2="16"/>
                            </svg>
                          </IconBtn>
                        )}
                        <IconBtn type="button" $del title={t('mapping:systems.toasts.deleted')} onClick={() => onDelete(sys)}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                            <path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/>
                          </svg>
                        </IconBtn>
                      </ActionsWrap>
                    </ActionCell>
                  </Tr>
                );
              })}
            </tbody>
          </Table>

          {totalPages > 0 && (
            <PaginationRow data-tour="pagination">
              <PaginationInfo>
                {t('mapping:systems.list.pagination.showing', { count: currentCount, total: totalItems })}
              </PaginationInfo>
              <PageButtons>
                <PageBtn onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="15 18 9 12 15 6"/>
                  </svg>
                </PageBtn>
                {pageButtons.map((btn, i) =>
                  btn === '…'
                    ? <PageBtn key={`e${i}`} disabled style={{ cursor: 'default' }}>…</PageBtn>
                    : <PageBtn key={btn} $active={btn === page} onClick={() => setPage(btn)}>{btn}</PageBtn>
                )}
                <PageBtn onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 18 15 12 9 6"/>
                  </svg>
                </PageBtn>
              </PageButtons>
            </PaginationRow>
          )}
        </TableCard>
      )}
    </Container>
  );
};
